<script setup lang="ts">
import Slider from 'primevue/slider';
import RadioButton from 'primevue/radiobutton';
import Checkbox from 'primevue/checkbox';
import Calendar from 'primevue/calendar';
import {
  useFundingNeedsForm,
  FUNDING_NEED_TYPE,
  MIN_FUNDING_AMOUNT,
  MAX_FUNDING_AMOUNT,
  MIN_FUNDING_AMOUNT_FORMATTED,
  MAX_FUNDING_AMOUNT_FORMATTED,
} from '@/composables/useFundingNeedsForm';
import RecurringNeedsInput from '@/components/forms/RecurringNeedsInput.vue';

const emit = defineEmits<{
  (e: 'submit-success'): void;
}>();

// ============================================================================
// Form Logic via Composable
// ============================================================================
const {
  // Form fields
  capitalAmount,
  fundingType,
  fundingTypeError,
  recurringNeeds,
  oneTimeNeedDate,
  capitalUsage,

  // Computed properties
  formattedCapitalAmount,
  capitalUsageOptions,
  defaultDates,
  isFormValid,
  hasFormChanged,

  // Methods
  formatDateForDisplay,
  submit,
} = useFundingNeedsForm(() => emit('submit-success'));

defineExpose({ submit, isFormValid, hasFormChanged });
</script>

<template>
  <div :class="$style.form">
    <div :class="$style.section">
      <h2 :class="$style.sectionTitle">
        Tell us your funding needs so we can tailor the best offer for your
        business:
      </h2>
    </div>

    <!-- Capital Amount Slider -->
    <div :class="$style.section">
      <div :class="$style.label">
        How much capital do you need approximately?
        <span :class="$style.required">*</span>
      </div>

      <div :class="$style.sliderContainer">
        <div :class="$style.sliderValue">
          ~
          {{
            formattedCapitalAmount +
            (capitalAmount === MAX_FUNDING_AMOUNT ? '+' : '')
          }}
        </div>
        <Slider
          v-model="capitalAmount"
          :min="MIN_FUNDING_AMOUNT"
          :max="MAX_FUNDING_AMOUNT"
          :step="10000"
          :class="$style.slider"
        />
        <div :class="$style.sliderLabels">
          <span>${{ MIN_FUNDING_AMOUNT_FORMATTED }}</span>
          <span>${{ MAX_FUNDING_AMOUNT_FORMATTED }}+</span>
        </div>
      </div>
    </div>

    <!-- Funding Type -->
    <div :class="$style.section">
      <div :class="$style.label">
        Is it a short-term need or an ongoing need for capital?
        <span :class="$style.required">*</span>
      </div>

      <div :class="$style.radioGroup">
        <div :class="$style.radioOption">
          <RadioButton
            v-model="fundingType"
            inputId="recurring"
            name="fundingType"
            :value="FUNDING_NEED_TYPE.RECURRING"
          />
          <label for="recurring" :class="$style.radioLabel">Ongoing</label>
        </div>

        <!-- Recurring Needs -->
        <div
          v-if="fundingType === FUNDING_NEED_TYPE.RECURRING"
          :class="[$style.recurringNeedsSection]"
        >
          <RecurringNeedsInput
            v-model="recurringNeeds"
            :default-dates="defaultDates"
            :format-date-for-display="formatDateForDisplay"
          />
        </div>

        <div :class="$style.radioOption">
          <RadioButton
            v-model="fundingType"
            inputId="one-time"
            name="fundingType"
            :value="FUNDING_NEED_TYPE.ONE_TIME"
          />
          <label for="one-time" :class="$style.radioLabel">Short-term</label>
        </div>

        <!-- One-time Need Date -->
        <div
          v-if="fundingType === FUNDING_NEED_TYPE.ONE_TIME"
          :class="[$style.oneTimeNeedDateSection]"
        >
          <div :class="$style.label">When will you need the capital by?</div>

          <div :class="$style.inputGroup">
            <div :class="[$style.inputGroupAddon, $style.inputGroupAddonLeft]">
              <i class="pi pi-calendar"></i>
            </div>
            <Calendar
              v-model="oneTimeNeedDate"
              dateFormat="mm/dd/yy"
              placeholder="mm/dd/yyyy"
              :class="$style.calendarInputField"
              :minDate="new Date()"
            />
          </div>
        </div>
      </div>

      <div v-if="fundingTypeError" :class="$style.error">
        {{ fundingTypeError }}
      </div>
    </div>

    <!-- Capital Usage -->
    <div :class="[$style.section, $style.capitalUsageSection]">
      <div :class="$style.label">What will you use the capital for?</div>

      <div :class="$style.checkboxGroup">
        <div
          v-for="option in capitalUsageOptions"
          :key="option"
          :class="$style.checkboxOption"
        >
          <Checkbox
            :inputId="option"
            :name="option"
            :value="option"
            v-model="capitalUsage"
          />
          <label :for="option" :class="$style.checkboxLabel">{{
            option
          }}</label>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" module>
.oneTimeNeedDateSection,
.recurringNeedsSection {
  margin-left: 8px;
}

.oneTimeNeedDateSection {
  border-left: 1px solid #e8e8ea;
  padding: 10px 0 14px 16px;
}

@media only screen and (max-width: 600px) {
  .oneTimeNeedDateSection,
  .recurringNeedsSection {
    margin-left: 0;
    border-left: none;
    padding-left: 0;
  }
}

.capitalUsageSection {
  padding-bottom: 40px;
}

.form {
  width: 585px;
  height: 535px;
  max-width: 100%;
}

.section {
  margin-bottom: 24px;
}

.sectionTitle {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 20px 0;
}

.label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 12px;
}

.required {
  color: #ef4444;
}

.sliderContainer {
  position: relative;
  margin-top: 24px;

  :global(.p-slider.p-slider-horizontal) {
    height: 8px;
  }
}

.sliderValue {
  text-align: center;
  font-size: 24px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 30px;
}

.sliderLabels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
  padding: 0 20px;
  transform: translateY(-8px);
}

.radioGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.radioOption {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}

.radioLabel {
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}

.inputGroup {
  display: flex;
  height: 38px;
  max-width: 100%;

  input {
    border-radius: 0;
  }
}

.inputGroupAddon {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: #f3f4f6;
  color: #6b7280;
  font-size: 14px;
  border-top: 1px solid #d1d5db;
  border-bottom: 1px solid #d1d5db;

  i {
    font-size: 14px;
  }
}

.inputGroupAddonLeft {
  border-left: 1px solid #d1d5db;
  border-radius: 6px 0 0 6px;
}

.calendarInputField {
  flex: 1;
}

.checkboxGroup {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 30px;
}

.checkboxOption {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checkboxLabel {
  font-size: 14px;
  color: #374151;
  cursor: pointer;
}

.error {
  color: #ef4444;
  font-size: 12px;
  margin-top: 6px;
}

.sliderContainer :global(.p-slider:not(.p-disabled) .p-slider-handle:hover) {
  background: #ffffff;
  border-color: #ffffff;
}
</style>
